import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { productRepository } from "@src/core/engine/products/productRepository";
import { QualifiedId } from "@src/core/engine/products/types";
import { RootState } from "@src/core/store";
import { PipeResults } from "@src/routes/projects/$projectId/selections/components/PipeResultsCard";
import {
  isSubProduct,
  subProductTypeToProductType,
} from "@src/routes/projects/$projectId/selections/direct/helpers/subProduct";
import { SubProductSuggestion } from "../$subProductType/contexts/SubProductsSuggestionsContext";
import { Suggestion } from "../contexts/SuggestionsContext";
import { CategoryFilters, Selection, TechnicalSelectionState } from "./types";

const technicalSelectionInitialState: TechnicalSelectionState = {
  categoryFilters: null,
  pipeResults: null,
  selectedProduct: null,
  selectedSubProduct: null,
  selections: {},
};

type SetSelectionAction = {
  disabledProducts: QualifiedId[];
  productId: string;
  selectedProducts: QualifiedId[];
  type: "actuator" | "product" | "sensor";
};

export const technicalSelectionSlice = createSlice({
  initialState: technicalSelectionInitialState,
  name: "technicalSelection",
  reducers: {
    clearSubProducts: (state) => {
      state.selectedSubProduct = null;
      Object.keys(state.selections).forEach((key) => {
        if (["actuator", "sensor"].includes(state.selections?.[key].type)) {
          delete state.selections?.[key];
        }
      });
    },
    resetTechnicalSelection: (state) => {
      state.categoryFilters = null;
      state.pipeResults = null;
      state.selectedProduct = null;
      state.selections = {};
    },
    setCategoryFilters: (
      state,
      action: PayloadAction<{
        filters: CategoryFilters;
        pipeResults: PipeResults;
      }>,
    ) => {
      state.categoryFilters = action.payload.filters;
      state.pipeResults = action.payload.pipeResults;
    },
    setProductSelection: (state, action: PayloadAction<SetSelectionAction>) => {
      const { disabledProducts, productId, selectedProducts, type } =
        action.payload;
      if (!state.selections) {
        state.selections = {};
      }
      state.selections[productId] = {
        disabledProductIds: disabledProducts,
        selectedProductIds: selectedProducts,
        type,
      };
    },
    setSelectedProduct: (state, action: PayloadAction<Suggestion>) => {
      state.selectedProduct = action.payload;
    },
    setSelectedSubProduct: (
      state,
      action: PayloadAction<SubProductSuggestion | null>,
    ) => {
      state.selectedSubProduct = action.payload;
    },
  },
});

const allSelectionsSelector = (state: RootState) =>
  state.technicalSelection.selections;

export const selectionSelector = createSelector(
  allSelectionsSelector,
  (_: RootState, productId: string) => productId,
  (selections, productId) => selections?.[productId],
);

export const typedSelectionSelector = createSelector(
  selectionSelector,
  (_: RootState, __: string, type: string) => type,
  (selection: Selection | undefined, type: string) => {
    if (!selection || !isSubProduct(type)) return [];

    return selection.selectedProductIds
      .filter((product) => product.type === subProductTypeToProductType(type))
      .map((product) => productRepository.getById(product));
  },
);

export const {
  clearSubProducts,
  resetTechnicalSelection,
  setCategoryFilters,
  setProductSelection,
  setSelectedProduct,
  setSelectedSubProduct,
} = technicalSelectionSlice.actions;
